
import { Vue, Component, Prop } from "vue-property-decorator";
import { Project_Activity } from "@/generated/graphql";
import moment from "moment";
import CardInfoChip from "@/components/card/CardInfoChip.vue";
import CalendarIcon from "@/components/icons/ClanderGray.vue";
import LocationIcon from "@/components/icons/ClockGray.vue";
import StarIcon from "@/components/icons/StartGray.vue";
import visitTypeDisplayMap from "@/entities/VisitType";
import EditTaskAction from "@/components/projects/activities/EditTaskAction.vue";
import DeleteTaskAction from "@/components/projects/activities/DeleteTask.vue";
import { getTimezoneFromLocationString, displayTimeInLocationTimezone, deserializeTimezone } from "@/utils/timezoneUtils";
import { VUE_GOOGLEMAP_API_KEY } from "@/data/constants";

@Component({
  components: {
    CardInfoChip,
    CalendarIcon,
    LocationIcon,
    StarIcon,
    EditTaskAction,
    DeleteTaskAction,
  },
})
export default class ContractorActivityCardWithProject extends Vue {
  @Prop({ type: Object, required: true }) readonly activity!: Project_Activity;
  @Prop({ type: String, required: true }) readonly currentUserId!: string;
  @Prop({ type: String, required: true }) readonly currentUserRole!: string;
  locationTimezone = null;
  get location(): string {
    const address = this.activity.location;
    return address && address.trim();
  }

  get isCompleted(): boolean {
    return this.activity.completed;
  }

  get createdAt(): string {
    const date = moment(this.activity.start_date).format("DD MMM, YYYY");
    return `${date}`;
  }

  async mounted() {
    await this.loadLocationTimezone();
  }

  async loadLocationTimezone() {
    // First try to use stored timezone from activity
    if (this.activity.timezone) {

      try {
        this.locationTimezone = deserializeTimezone(this.activity.timezone);
        if (this.locationTimezone) {
          return; // Successfully loaded from stored data
        }
      } catch (error) {
        console.error("Error deserializing stored timezone:", error);
      }
    }

    // Fall back to Google Maps API if no stored timezone or deserialization failed
    if (this.location) {
      try {
        this.locationTimezone = await getTimezoneFromLocationString(this.location, VUE_GOOGLEMAP_API_KEY);
      } catch (error) {
        console.error("Failed to load location timezone:", error);
      }
    }
  }

  get startTime(): string {
    if (!this.activity?.start_time || !this.locationTimezone) {
      return "n/a";
    }

    try {
      return displayTimeInLocationTimezone(this.activity.start_time, this.locationTimezone, "h:mm A");
    } catch (error) {
      return "Invalid Time";
    }
  }

  get endTime(): string {
    if (!this.activity?.end_time || !this.locationTimezone) {
      return "n/a";
    }

    try {
      return displayTimeInLocationTimezone(this.activity.end_time, this.locationTimezone, "h:mm A");
    } catch (error) {
      return "Invalid Time";
    }
  }

  get totalHours(): string {
    if (!this.activity?.start_time || !this.activity?.end_time) {
      return;
    }

    const startTimeString = this.formatTime(this.activity.start_time);
    const endTimeString = this.formatTime(this.activity.end_time);

    if (!startTimeString || !endTimeString) {
      return "Invalid Time";
    }

    try {
      const startTime = this.$moment(startTimeString);
      const endTime = this.$moment(endTimeString);

      if (!startTime.isValid() || !endTime.isValid()) {
        return "Invalid Time";
      }

      const durationInMinutes = endTime.diff(startTime, "minutes");
      if (durationInMinutes < 0) {
        return "Invalid Time";
      }

      const hours = Math.floor(durationInMinutes / 60);
      const minutes = durationInMinutes % 60;

      if (hours > 0 && minutes > 0) {
        return `(${hours}h ${minutes}m)`;
      } else if (hours > 0) {
        return `(${hours} hours)`;
      } else {
        return `(${minutes}m)`;
      }
    } catch (error) {
      console.error("Error calculating time difference:", error);
      return "Invalid Time";
    }
  }

  formatTime(time: any): string | null {
    if (typeof time === "string") {
      return time;
    }

    if (time && time.HH && time.mm) {
      return `${time.HH}:${time.mm}`;
    }

    return null;
  }

  getVisitType(type: string | undefined): string {
    if (!type) return "n/a";
    return (
      visitTypeDisplayMap[type] ||
      type
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase())
    );
  }

  get typeOfVisit(): string {
    return this.getVisitType(this.activity?.type_of_visit);
  }
  visitUpdated(): void {
    this.$emit("visitUpdated");
  }

}
